import ReactPaginate from "react-paginate";
import { RootState } from "../../store";
import { useSelector } from "react-redux";

interface PaginateProps {
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  loading: boolean; // Add loading prop
  displayTotalRecords?: boolean;
  title?:string;
  marginTop?:string;
  /** "yellow" uses the dark #332B00 page numbers from the MNO list redesign. */
  variant?: "default" | "yellow";
}

export default function Paginate({ totalPages, currentPage, onPageChange, loading, displayTotalRecords, title, marginTop, variant = "default" }: PaginateProps) {
  const listState = useSelector((state: RootState) => state.list);
  const { recordCount } = listState;
  const isYellow = variant === "yellow";
  const isFirstDisabled = currentPage === 1 || loading;
  const isLastDisabled = currentPage === totalPages || loading;
  // Yellow (MNO list) design uses Figma's carets: #332B00, or pale yellow #FFF4C9 when disabled
  const previousIcon = isYellow
    ? isFirstDisabled ? "yellow-caret-left-disabled" : "yellow-caret-left"
    : isFirstDisabled ? "page-light-right" : "page-dark-right";
  const nextIcon = isYellow
    ? isLastDisabled ? "yellow-caret-right-disabled" : "yellow-caret-right"
    : isLastDisabled ? "page-light-left" : "page-dark-left";
  // Figma: 24px carets with an 8px gap to 40px page boxes
  const arrowBoxClass = isYellow ? "w-6 h-6" : "w-[28px] h-[28px] sm:w-[36px] sm:h-[36px]";
  const pageBoxClass = isYellow
    ? "w-10 h-10 text-sm leading-6 text-[#332b00]"
    : "text-xs sm:text-sm text-primary_text p-1 sm:p-2 min-w-[14px] sm:min-w-[20px] h-5 mx-0.5 sm:mx-1";
  return (
    <div className={`flex flex-wrap items-center justify-center z-9 cursor-default relative mt-${marginTop} gap-y-2`}>
      <div className="flex items-center rounded-lg p-1 cursor-default w-full justify-center" data-testid="paginator">
        <ReactPaginate
          previousLabel={
            <div
              className={`rounded-lg ${arrowBoxClass} ${isYellow ? "mr-2" : ""} flex justify-center prev bg-transparent ${loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
            >
              <img
                loading="lazy"
                decoding="async"
                src={`/images/${previousIcon}.svg`}
                alt="Previous"
                data-testid="previous"
                className={isYellow ? "w-6" : "w-7"}
              />
            </div>
          }
          nextLabel={
            <div
              className={`rounded-lg ${arrowBoxClass} ${isYellow ? "ml-2" : ""} flex justify-center next bg-transparent ${loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
            >
              <img
                loading="lazy"
                decoding="async"
                src={`/images/${nextIcon}.svg`}
                alt="Next"
                data-testid="next"
                className={isYellow ? "w-6" : "w-7"}
              />
            </div>
          }
          breakLabel="..."
          breakClassName={isYellow ? `${pageBoxClass} inline-flex justify-center items-center` : undefined}
          pageCount={totalPages}
          marginPagesDisplayed={2}
          pageRangeDisplayed={3}
          forcePage={currentPage - 1}
          onPageChange={(selected) => !loading && onPageChange(selected.selected + 1)}
          containerClassName={`flex justify-center items-center ${loading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          pageClassName={`font-normal ${pageBoxClass} inline-flex justify-center items-center ${loading ? "pointer-events-none" : ""
            }`}
          activeClassName={`${isYellow ? "text-[#332b00]!" : "text-p_primary!"} font-bold!`}
          pageLinkClassName={isYellow ? "w-full h-full inline-flex justify-center items-center" : "p-1 sm:p-2"}
        />
      </div>
      {displayTotalRecords && (
        <p className="w-full text-center sm:w-auto sm:absolute sm:top-1/2 sm:-translate-y-1/2 sm:right-0 text-body2 text-primary_text">
          {`Total Number of ${title} : `}<span className="text-button text-primary_text font-semibold">{recordCount}</span>
        </p>
      )}
    </div>
  );
}
